/**
 * BouncingLogoLoader — animated E-Mina mine logo preloader.
 *
 * Four variants (admin-selectable via HomepageSettings → preloader_animation):
 *   - "bounce" : vertical jump with soft shadow that squashes on landing
 *   - "pulse"  : explosion-like scale pulse (mine ticking) with red ring
 *   - "wobble" : rotation wobble with small sparks/particles around the mine
 *   - "mina"   : default Emina mina — charge builds up then EXPLODES exactly
 *                once when the page finishes loading (opens to the actual page)
 *
 * Props:
 *   - variant  : "bounce" | "pulse" | "wobble" | "mina"  (default: "mina")
 *   - size     : logo width in px (default 96)
 *   - fullscreen: boolean — when true, covers the viewport with a dark backdrop
 *   - label    : optional sub-caption shown under the logo
 */
import { useEffect, useState } from 'react';

const LOGO_SRC = '/emina-mine-logo.png';

/**
 * BouncingLogoLoader — now supports a one-time EXPLODE exit animation when
 * the `exiting` prop flips to true (see `ExplodingPreloader` helper below).
 */
export function BouncingLogoLoader({ variant = 'mina', size = 96, fullscreen = false, label = null, exiting = false }) {
  const wrapperClass = fullscreen
    ? `fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-sm preloader-backdrop ${exiting ? 'is-exploding' : ''}`
    : `relative flex flex-col items-center justify-center py-10 ${exiting ? 'is-exploding' : ''}`;

  return (
    <div className={wrapperClass} data-testid={`preloader-${variant}`} role="status" aria-live="polite">
      {variant === 'pulse' && <PulseVariant size={size} />}
      {variant === 'wobble' && <WobbleVariant size={size} />}
      {variant === 'bounce' && <BounceVariant size={size} />}
      {variant === 'mina' && <MinaVariant size={size} />}
      {/* White flash overlay — opacity is driven by `.is-exploding` class */}
      {fullscreen && <span className="preloader-flash" aria-hidden="true" />}
      {label && <p className="mt-4 text-xs font-semibold tracking-wider text-slate-300 uppercase">{label}</p>}
      <span className="sr-only">Učitavanje…</span>
    </div>
  );
}

/**
 * ExplodingPreloader — wraps BouncingLogoLoader and holds it mounted for a
 * short "explode" animation after `visible` flips to false. Usage:
 *
 *   const [loading, setLoading] = useState(true);
 *   fetchData().finally(() => setLoading(false));
 *   return <ExplodingPreloader visible={loading} variant="mina" fullscreen />;
 */
export function ExplodingPreloader({ visible, variant = 'mina', size = 96, fullscreen = false, label = null }) {
  const [mounted, setMounted] = useState(!!visible);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setExiting(false);
    } else if (mounted) {
      // Play explode animation for ~750ms before unmounting
      setExiting(true);
      const t = setTimeout(() => { setMounted(false); setExiting(false); }, 820);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!mounted) return null;
  return <BouncingLogoLoader variant={variant} size={size} fullscreen={fullscreen} label={label} exiting={exiting} />;
}

function BounceVariant({ size }) {
  return (
    <div className="relative" style={{ width: size, height: size + 32 }}>
      <img src={LOGO_SRC} alt="" width={size} height={size}
           className="emina-loader-bounce block mx-auto drop-shadow-[0_6px_14px_rgba(220,38,38,0.35)]"
           style={{ width: size, height: size }} />
      <span className="emina-loader-shadow absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-black/60 blur-md"
            style={{ bottom: 0, width: size * 0.7, height: 10 }} />
    </div>
  );
}

function PulseVariant({ size }) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: size * 1.6, height: size * 1.6 }}>
      <span className="emina-loader-ring absolute inset-0 rounded-full border-2 border-red-500/40" />
      <span className="emina-loader-ring-2 absolute inset-4 rounded-full border border-red-400/30" />
      <img src={LOGO_SRC} alt="" width={size} height={size}
           className="emina-loader-pulse relative z-10 drop-shadow-[0_0_18px_rgba(220,38,38,0.65)]"
           style={{ width: size, height: size }} />
    </div>
  );
}

function WobbleVariant({ size }) {
  return (
    <div className="relative" style={{ width: size * 1.7, height: size * 1.7 }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <span key={i}
              className="emina-loader-spark absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-red-500"
              style={{ transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-${size * 0.75}px)`,
                       animationDelay: `${i * 0.12}s` }} />
      ))}
      <img src={LOGO_SRC} alt="" width={size} height={size}
           className="emina-loader-wobble absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)]"
           style={{ width: size, height: size }} />
    </div>
  );
}

/**
 * Mina variant — charges up (shakes + glows) during loading, then fires a
 * single EXPLOSION that opens into the page. The `unmount` event (when the
 * loader disappears) naturally serves as "page loaded" — so we fire the
 * explosion on unmount via a 400 ms fade-out animation driven by CSS-only.
 */
function MinaVariant({ size }) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: size * 2.4, height: size * 2.4 }}>
      {/* Radial shockwave rings that expand + fade (repeat while mounted) */}
      <span className="emina-mina-ring absolute inset-0 rounded-full border-2 border-red-500/40" />
      <span className="emina-mina-ring-2 absolute inset-4 rounded-full border border-red-400/30" />
      <span className="emina-mina-ring-3 absolute inset-8 rounded-full border border-amber-400/30" />
      {/* Scatter particles radiating outward — simulates explosion fragments */}
      {Array.from({ length: 12 }).map((_, i) => (
        <span key={i}
              className="emina-mina-frag absolute left-1/2 top-1/2 w-1 h-1 rounded-full bg-red-400"
              style={{
                transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-${size * 0.55}px)`,
                animationDelay: `${(i % 4) * 0.15}s`,
                background: i % 3 === 0 ? '#f59e0b' : (i % 3 === 1 ? '#dc2626' : '#facc15'),
              }} />
      ))}
      {/* The mine itself ticks (scale/shake) until explode */}
      <img src={LOGO_SRC} alt="" width={size} height={size}
           className="emina-mina-core relative z-10 drop-shadow-[0_0_22px_rgba(220,38,38,0.75)]"
           style={{ width: size, height: size }} />
    </div>
  );
}

// Keyframes + animation classes live in /app/frontend/src/index.css.
// They were previously inlined here via a <style> child but that caused
// React hydration warnings ("<style> cannot be a child of <span>").

const ALLOWED = ['bounce', 'pulse', 'wobble', 'mina'];
const DEFAULT_VARIANT = 'mina';

/**
 * Small hook that reads the current preloader animation from public homepage
 * settings (cached to sessionStorage to avoid jank on every load).
 */
export function usePreloaderVariant() {
  const [variant, setVariant] = useState(() => {
    try {
      const v = sessionStorage.getItem('emina_preloader_variant');
      return ALLOWED.includes(v) ? v : DEFAULT_VARIANT;
    } catch { return DEFAULT_VARIANT; }
  });

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/homepage-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        const v = data?.preloader?.animation;
        if (v && ALLOWED.includes(v)) {
          setVariant(v);
          try { sessionStorage.setItem('emina_preloader_variant', v); } catch {}
        }
      }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return variant;
}

export default BouncingLogoLoader;
